import clsx from 'clsx';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toggleSwitch } from '../../reducers/switch';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import WarningIcon from '../../assets/icons/warning-icon.svg?react';
import AddIcon from '../../assets/icons/add-icon.svg?react';
import {
  GetAllIncidentTypesResponse,
  IncidentTypes,
} from '@EHSNavigator/backend';
import CrossIcon from '../../assets/icons/sidebar-cross-icon.svg?react';
import EditIcon from '../../assets/icons/edit-icon.svg?react';
import Button from '../../components/Button';
import { useButtonSizeMdSmaller } from '../../utils/buttonSizes';
import ModalComponent from '../../components/ModalComponent';
import { toggleModal } from '../../reducers/modalToggle';
import AddCustomIncidentTypeModal from '../../components/AddCustomIncidentTypeModal';
import {
  getCustomIncidentTypeSchema,
  getDeleteCustomIncidentTypeSchema,
} from './schema';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { useApiClients } from '../../client/api/useApiClient';
import { queryClient } from '../../main';
import EditCustomIncidentTypeModal from '../../components/EditCustomIncidentTypeModal';
import DeleteCustomIncidentTypeModal from '../../components/DeleteCustomIncidentTypeModal';

interface CustomIncidentTypesProps {
  otherClasses?: string;
  data: GetAllIncidentTypesResponse['incidentTypes'];
}

const CustomIncidentTypes: React.FC<CustomIncidentTypesProps> = ({
  otherClasses,
  data,
}) => {
  const customIncidentTypesClasses = clsx(
    otherClasses,
    'px-15 md:px-6 lg:pl-8 pt-14 lg:pr-14 pb-9 w-full',
  );
  const { CustomIncidentTypesApi } = useApiClients();

  const dispatch = useDispatch();
  const [currentIncidentType, setCurrentIncidentType] =
    useState<IncidentTypes | null>(null);
  const modalName = useSelector(
    (state: { modalToggle: { modalName: string } }) =>
      state.modalToggle.modalName,
  );
  const lg = useButtonSizeMdSmaller();

  const addCustomIncidentTypeHandler = useFormik({
    initialValues: {
      name: '',
    },
    validationSchema: getCustomIncidentTypeSchema(),
    onSubmit: async (values) => {
      try {
        const res =
          await CustomIncidentTypesApi.customIncidentTypesControllerCreate({
            createCustomIncidentTypeDto: {
              name: values.name,
            },
          });
        queryClient.setQueryData(['all-incident-types'], (oldData: any) => {
          const updatedIncidentTypes = [res?.data?.incidentType, ...oldData];
          return updatedIncidentTypes;
        });
        toast.success(res?.data?.message);
        addCustomIncidentTypeHandler.resetForm();
        dispatch(toggleModal(''));
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });
  const editCustomIncidentTypeHandler = useFormik({
    initialValues: {
      name: '',
    },
    validationSchema: getCustomIncidentTypeSchema(),
    onSubmit: async (values) => {
      try {
        const res =
          await CustomIncidentTypesApi.customIncidentTypesControllerUpdate({
            id: currentIncidentType?.id as string,
            updateCustomIncidentTypeDto: {
              name: values.name,
            },
          });

        queryClient.setQueryData(['all-incident-types'], (oldData: any) => {
          const updatedIncidentTypes = oldData.map((node: IncidentTypes) =>
            node.id === currentIncidentType?.id
              ? res?.data?.incidentType
              : node,
          );
          return updatedIncidentTypes;
        });
        toast.success(res?.data?.message);
        editCustomIncidentTypeHandler.resetForm();
        setCurrentIncidentType(null);
        dispatch(toggleModal(''));
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });
  const deleteCustomIncidentTypeHandler = useFormik({
    initialValues: {
      name: '',
    },
    validationSchema: getDeleteCustomIncidentTypeSchema(
      currentIncidentType?.name || '',
    ),
    onSubmit: async () => {
      try {
        const res =
          await CustomIncidentTypesApi.customIncidentTypesControllerRemove({
            id: currentIncidentType?.id as string,
          });

        queryClient.setQueryData(['all-incident-types'], (oldData: any) => {
          const updatedIncidentTypes = oldData.filter(
            (node: IncidentTypes) => node.id !== currentIncidentType?.id,
          );
          return updatedIncidentTypes;
        });
        toast.success(res?.data?.message);
        deleteCustomIncidentTypeHandler.resetForm();
        setCurrentIncidentType(null);
        dispatch(toggleModal(''));
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  return (
    <div
      className={customIncidentTypesClasses}
      data-testid="custom-incident-types"
    >
      <div className="border-b-neutral-tones-text/35 border-b pb-6 lg:pb-12">
        <h3 className="hidden lg:block">Custom Categories/Types</h3>
        <div className="flex items-center gap-3 lg:hidden">
          <button
            className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5"
            onClick={() => dispatch(toggleSwitch())}
          >
            <ToggleIcon />
          </button>
          <h3 className="lg:hidden">Custom Categories/Types</h3>
        </div>
        <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-lg leading-5 font-medium lg:block lg:text-xl lg:leading-6">
          Create and define new categories for incident types which user's can
          select while reporting incidents.
        </p>
      </div>
      <div className="rounded-10 bg-card-backgrounds/5 mt-6 px-6 py-15 lg:mt-12">
        {data?.length > 0 ? (
          <>
            <h6 className="font-inter-variable text-neutral-tones-text text-lg leading-6 font-bold lg:text-xl">
              List of All Incident Types
            </h6>
            <ul className="border-t-neutral-tones-text/35 mt-15 grid grid-cols-1 gap-x-10 gap-y-15 border-t py-15 sm:grid-cols-2 xl:gap-x-75">
              {data?.map((node, index) => (
                <li
                  key={index}
                  title={node?.name}
                  className={clsx(
                    'border-b-neutral-tones-text/10 flex items-center justify-between gap-15 border-b pb-15',
                    data?.length === 1 && 'col-span-1 sm:col-span-2',
                  )}
                >
                  <p className="text-neutral-tones-text font-inter-variable line-clamp-1 text-base leading-5 font-normal capitalize lg:text-lg">
                    {node?.name}
                  </p>
                  <div className="flex items-center gap-15">
                    <button
                      onClick={() => {
                        setCurrentIncidentType(node);
                        dispatch(toggleModal('edit-custom-incident-type'));
                      }}
                      className="group flex cursor-pointer items-baseline disabled:cursor-not-allowed"
                    >
                      <EditIcon className="[&>path]:fill-brand-byzantine h-5 w-5" />
                    </button>
                    <button
                      onClick={() => {
                        setCurrentIncidentType(node);
                        dispatch(toggleModal('delete-custom-incident-type'));
                      }}
                      className="group flex cursor-pointer items-baseline disabled:cursor-not-allowed"
                    >
                      <CrossIcon className="[&>g>path]:fill-neutral-tones-text/50 group-hover:[&>g>path]:fill-brand-posh-pink h-5 w-5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <Button
              size={lg}
              onClick={() => dispatch(toggleModal('add-custom-incident-type'))}
            >
              <AddIcon className="h-6 w-6 [&>path]:fill-current" />
              Add New
            </Button>
          </>
        ) : (
          <div className="items-cemter flex h-500 flex-col items-center justify-center gap-15 text-center">
            <WarningIcon className="[&>g>path]:fill-brand-byzantine h-12 w-12 md:h-85 md:w-85" />
            <h3 className="">No categories found</h3>

            <Button
              size={lg}
              onClick={() => dispatch(toggleModal('add-custom-incident-type'))}
            >
              <AddIcon className="h-6 w-6 [&>path]:fill-current" />
              Add New
            </Button>
          </div>
        )}
      </div>
      {(modalName === 'add-custom-incident-type' ||
        modalName === 'edit-custom-incident-type' ||
        modalName === 'delete-custom-incident-type') && (
        <ModalComponent
          otherClasses="px-4 sm:px-0 "
          additionalClass={clsx(
            'w-full max-w-573 rounded-2xl p-5 lg:px-9 lg:py-6 ',
          )}
          customClose={() => {
            addCustomIncidentTypeHandler.resetForm();
            editCustomIncidentTypeHandler.resetForm();
            deleteCustomIncidentTypeHandler.resetForm();
            setCurrentIncidentType(null);
            dispatch(toggleModal(''));
          }}
        >
          {modalName === 'add-custom-incident-type' && (
            <AddCustomIncidentTypeModal
              formHandler={addCustomIncidentTypeHandler}
              handleClose={() => {
                addCustomIncidentTypeHandler.resetForm();
                setCurrentIncidentType(null);
                dispatch(toggleModal(''));
              }}
            />
          )}
          {modalName === 'edit-custom-incident-type' && (
            <EditCustomIncidentTypeModal
              currentIncidentType={currentIncidentType}
              formHandler={editCustomIncidentTypeHandler}
              handleClose={() => {
                editCustomIncidentTypeHandler.resetForm();
                setCurrentIncidentType(null);
                dispatch(toggleModal(''));
              }}
            />
          )}
          {modalName === 'delete-custom-incident-type' && (
            <DeleteCustomIncidentTypeModal
              currentIncidentType={currentIncidentType}
              formHandler={deleteCustomIncidentTypeHandler}
              handleClose={() => {
                deleteCustomIncidentTypeHandler?.resetForm();
                setCurrentIncidentType(null);
                dispatch(toggleModal(''));
              }}
            />
          )}
        </ModalComponent>
      )}
    </div>
  );
};

export default CustomIncidentTypes;
